// components/chat/chat-input/ChatInputBar.tsx
import React, { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { LexicalEditorWrapper, LexicalEditorHandle } from "./LexicalEditor";
import { AttachmentPreview } from "./AttachmentPreview";
import { AttachmentUploadButton } from "./AttachmentUploadButton";
import { MicButton } from "./MicButton";
import { SendButton } from "./SendButton";
import { ModelSelector } from "./ModelSelector";
import { AddContextButton, AddContextButtonHandle } from "./AddContextButton";
import { CornerDownRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ContextItem } from "@/components/shared/AddContextMenu";
import type { Plan } from "@/lib/ai/models";

export interface ContextReference {
  text: string;
  onClear: () => void;
}

export interface ChatInputBarProps {
  isMobile?: boolean;
  onSendMessage?: (text: string, attachments: File[]) => Promise<void>;
  disabled?: boolean;
  replyToText?: string | null;
  onClearReply?: () => void;
  // NEW props for flexibility
  showModelSelector?: boolean;
  showContextButton?: boolean;
  showAttachments?: boolean;
  showMicButton?: boolean;
  placeholder?: string;
  contextReference?: ContextReference | null;
  variant?: "default" | "compact";
  className?: string;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  onRecordingComplete?: (blob: Blob) => void;
  onMentionSelect?: (toolId: string) => void;
  onContextSelect?: (item: ContextItem) => void;
  onSearchStateChange?: (enabled: boolean) => void;
  userPlan?: Plan;
  onUpgradeRequest?: () => void;
}

export const ChatInputBar: React.FC<ChatInputBarProps> = ({
  isMobile = false,
  onSendMessage,
  disabled = false,
  replyToText,
  onClearReply,
  showModelSelector = true,
  showContextButton = true,
  showAttachments = true,
  showMicButton = true,
  placeholder = "Learn anything",
  contextReference,
  variant = "default",
  className,
  selectedModel,
  onModelChange,
  onRecordingComplete,
  onMentionSelect,
  onContextSelect,
  onSearchStateChange,
  userPlan,
  onUpgradeRequest,
}) => {
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [clearTrigger, setClearTrigger] = useState(0);
  const [sendTrigger, setSendTrigger] = useState(0);
  const contextButtonRef = useRef<AddContextButtonHandle>(null);
  const editorRef = useRef<LexicalEditorHandle>(null);

  const handleSend = async (text: string) => {
    if (!text.trim() && attachments.length === 0) return;
    if (disabled || isSending) return;

    setIsSending(true);
    // Include reply context in the message if present
    let fullMessage = text;
    if (replyToText) {
      fullMessage = `Regarding: "${replyToText}"\n\n${text}`;
    } else if (contextReference) {
      fullMessage = `Regarding: "${contextReference.text}"\n\n${text}`;
    }
    await onSendMessage?.(fullMessage, attachments);
    setIsSending(false);
    setAttachments([]);
    onClearReply?.();
    contextReference?.onClear();
  };

  const handleSendButtonClick = () => {
    setSendTrigger((prev) => prev + 1);
  };

  const handleFilesAdded = (files: File[]) => {
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleRemoveAttachment = (file: File) => {
    setAttachments((prev) => prev.filter((f) => f !== file));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission will be handled by SendButton click
  };

  // Open context menu programmatically (for @ trigger)
  const handleOpenContextMenu = useCallback(() => {
    contextButtonRef.current?.openMenu();
  }, []);

  // Handle mention insert from context menu
  const handleMentionInsert = useCallback((item: ContextItem) => {
    editorRef.current?.insertMention(item);
  }, []);

  // Track selected context items and notify parent about search state
  const handleSelectedItemsChange = useCallback((items: ContextItem[]) => {
    const hasSearch = items.some((item) => item.id === "search");
    onSearchStateChange?.(hasSearch);
  }, [onSearchStateChange]);

  // Handle recording completion → transcription → editor insertion
  const handleRecordingComplete = useCallback(async (blob: Blob) => {
    if (!blob || blob.size === 0 || isTranscribing) return;

    setIsTranscribing(true);
    try {
      const ext = blob.type.includes("ogg") ? "ogg" : "webm";
      const formData = new FormData();
      formData.append("file", blob, `recording.${ext}`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      // Get auth token
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${supabaseUrl}/functions/v1/transcribe-audio`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token || supabaseKey}`,
          apikey: supabaseKey,
        },
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: "Transcription failed" }));
        if (res.status === 429) {
          toast.error("Too many requests, please try again later.");
        } else if (res.status === 504) {
          toast.error("Transcription timed out. Please try again.");
        } else {
          toast.error(errData.error || "Could not transcribe audio. Please try again.");
        }
        return;
      }

      const data = await res.json();
      if (data.text) {
        editorRef.current?.insertTextAtCursor(data.text + " ");
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        toast.error("Transcription timed out. Please try again.");
      } else {
        toast.error("Network error. Please check your connection.");
      }
    } finally {
      setIsTranscribing(false);
    }
  }, [isTranscribing]);

  const isCompact = variant === "compact";

  return (
    <form
      onSubmit={handleFormSubmit}
      className={cn(
        "relative mx-auto mb-2 sm:mb-6 mt-0 flex w-full flex-col items-end justify-center space-y-1 rounded-3xl border bg-card px-4 py-2 shadow-sm transition-all duration-150 focus-within:border-primary/20 hover:border-primary/10 sm:px-3",
        disabled && "opacity-60 cursor-not-allowed",
        isCompact && "mb-0 py-1.5",
        className
      )}
    >
      <div className="flex w-full flex-col">
        {/* Reply To Preview */}
        {replyToText && (
          <div className="mt-1 flex w-full items-center justify-between rounded rounded-t-md bg-muted/70 p-2 text-primary/70">
            <div className="flex items-center gap-2 min-w-0">
              <CornerDownRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span className="line-clamp-3 text-sm">{replyToText}</span>
            </div>
            <button
              type="button"
              onClick={onClearReply}
              className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg p-0.5 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Context Reference (e.g., for exam questions) */}
        {contextReference && !replyToText && (
          <div className="mt-1 flex w-full items-center justify-between rounded rounded-t-md bg-muted/70 p-2 text-primary/70">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <CornerDownRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm leading-6 line-clamp-3">{contextReference.text}</p>
            </div>
            <button
              type="button"
              onClick={contextReference.onClear}
              className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg p-0.5 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none ml-2"
            >
              <X className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Attachments */}
        {showAttachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((file, idx) => (
              <AttachmentPreview
                key={idx}
                file={file}
                onRemove={() => handleRemoveAttachment(file)}
              />
            ))}
          </div>
        )}

        {/* Text area */}
        <div className="mt-2 flex w-full items-center">
          <div className="relative w-full text-sm">
            <LexicalEditorWrapper 
              editorRef={editorRef}
              onSend={handleSend} 
              disabled={disabled || isSending}
              clearTrigger={clearTrigger}
              sendTrigger={sendTrigger}
              placeholder={placeholder}
              onMentionSelect={onMentionSelect}
              onContextSelect={onContextSelect}
              onOpenContextMenu={handleOpenContextMenu}
            />
          </div>
        </div>

        {/* Bottom row: model select, context, attachments, mic, send button */}
        <div className="flex flex-row items-end justify-between opacity-70 transition-all">
          {/* Left controls */}
          <div className="mb-0.5 flex flex-wrap items-center space-x-1">
            {showModelSelector && (
              <ModelSelector 
                selectedModel={selectedModel}
                onModelChange={onModelChange}
                userPlan={userPlan}
                onUpgradeRequest={onUpgradeRequest}
              />
            )}
            {showContextButton && (
              <AddContextButton 
                ref={contextButtonRef}
                onContextSelect={onContextSelect}
                onMentionInsert={handleMentionInsert}
                onSelectedItemsChange={handleSelectedItemsChange}
              />
            )}
          </div>

          {/* Right controls */}
          <div className="flex items-center">
            {showAttachments && (
              <AttachmentUploadButton onFilesAdded={handleFilesAdded} />
            )}
            {showMicButton && (
              <MicButton 
                onRecordingComplete={handleRecordingComplete}
                disabled={disabled || isSending || isTranscribing}
                isTranscribing={isTranscribing}
              />
            )}
            <SendButton 
              disabled={disabled || isSending} 
              onClick={handleSendButtonClick}
            />
          </div>
        </div>
      </div>
    </form>
  );
};
